import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './styles/index.scss';

import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import PublicRoute from './components/PublicRoute';
import AuthProvider from './components/AuthProvider';
import { LanguageProvider } from './contexts/LanguageContext';
import { detectAppMode, isAppMode } from './utils/appMode';

// 공용 페이지
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Home from './pages/Home';
import OneDayClass from './pages/non-member/OneDayClass';
import LessonRequest from './pages/non-member/LessonRequest';
import Contact from './pages/non-member/Contact';
import AcademyTour from './pages/non-member/AcademyTour';
import Instructors from './pages/Instructors';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFound from './pages/NotFound';

// 사용자 페이지
import UserDashboard from './pages/user/Dashboard';
import UserLessonSchedule from './pages/user/UserLessonSchedule';
import UserPracticeBooking from './pages/user/PracticeBooking';
import ChangePassword from './pages/user/ChangePassword';
import UserLessonLogs from './pages/user/LessonLogs';

// 선생님 페이지
import Dashboard from './pages/teacher/Dashboard';
import Users from './pages/teacher/Users';
import LessonRequests from './pages/teacher/LessonRequests';
import OneDayRequests from './pages/teacher/OneDayRequests';
import LessonModifications from './pages/teacher/LessonModifications';
import Contacts from './pages/teacher/Contacts';
import LessonSchedule from './pages/teacher/LessonSchedule';
import PracticeBooking from './pages/teacher/PracticeBooking';
import LessonLogWrite from './pages/teacher/LessonLogWrite';
import LessonLog from './pages/teacher/LessonLog';
import TeacherRoute from './components/TeacherRoute';
import UserRoute from './components/UserRoute';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// 앱 모드 감지 (최초 1회)
detectAppMode();

// 앱 모드에서 공개 페이지를 로그인으로 리다이렉트
function AppModeGuard({ children }: { children: React.ReactNode }) {
  if (isAppMode()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Footer를 조건부로 렌더링하는 컴포넌트
function ConditionalFooter() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isUserPage = location.pathname.startsWith('/user');

  // 404 페이지는 현재 경로가 정의된 라우트에 없을 때 표시됨
  const isNotFoundPage = !['/', '/one-day-class', '/lesson-request', '/contact', '/contact/check', '/academy-tour', '/instructors', '/privacy-policy', '/terms-of-service', '/login', '/admin', '/user'].some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // 로그인 페이지, 404 페이지, 선생님/관리자 페이지, 유저 페이지에서는 푸터 숨김
  if (isLoginPage || isNotFoundPage || isAdminPage || isUserPage) {
    return null;
  }

  return <Footer />;
}

// Flutter에서 React Router 네비게이션을 사용할 수 있게 하는 컴포넌트
function NavigateHelper() {
  const navigate = useNavigate();

  useEffect(() => {
    (window as any).navigateFromFlutter = (route: string) => {
      navigate(route);
    };
  }, [navigate]);

  return null;
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <AuthProvider>
            <NavigateHelper />
            <ScrollToTop />
            <div className="App">
              <Routes>
                {/* 공개 라우트 - 앱 모드에서는 로그인으로 리다이렉트 */}
                <Route path="/" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <Home />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/one-day-class" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <OneDayClass />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/lesson-request" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <LessonRequest />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/contact" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <Contact />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                {/* 문의 조회는 문의 · Q&A 페이지로 통합됨 */}
                <Route path="/contact/check" element={<Navigate to="/contact" replace />} />
                <Route path="/academy-tour" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <AcademyTour />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/instructors" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <Instructors />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/privacy-policy" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <PrivacyPolicy />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/terms-of-service" element={
                  <AppModeGuard>
                    <PublicLayout>
                      <TermsOfService />
                    </PublicLayout>
                  </AppModeGuard>
                } />
                <Route path="/login" element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } />
                <Route path="/forgot-password" element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                } />

                {/* 404 페이지 - 모든 라우트의 마지막에 위치 */}
                <Route path="*" element={<NotFound />} />

                {/* 선생님/관리자 보호된 라우트 */}
                <Route path="/admin" element={
                  <TeacherRoute>
                    <Layout />
                  </TeacherRoute>
                }>
                  <Route index element={<Navigate to={isAppMode() ? "/admin/lesson-schedule" : "/admin/dashboard"} replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="users" element={<Users />} />
                  <Route path="lesson-requests" element={<LessonRequests />} />
                  <Route path="oneday-requests" element={<OneDayRequests />} />
                  <Route path="lesson-modifications" element={<LessonModifications />} />
                  <Route path="contacts" element={<Contacts />} />
                  <Route path="practice-booking" element={<PracticeBooking />} />
                  <Route path="lesson-schedule" element={<LessonSchedule />} />
                  <Route path="lesson-log" element={<LessonLog />} />
                  <Route path="lesson-log/write" element={<LessonLogWrite />} />
                </Route>

                {/* 이전 라우트와의 호환성을 위한 리다이렉트 */}
                <Route path="/teacher/*" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/dashboard/*" element={<Navigate to="/admin/dashboard" replace />} />

                {/* 사용자 보호된 라우트 */}
                <Route path="/user" element={
                  <UserRoute>
                    <Layout />
                  </UserRoute>
                }>
                  <Route index element={<Navigate to="/user/dashboard" replace />} />
                  <Route path="dashboard" element={<UserDashboard />} />
                  <Route path="lesson-logs" element={<UserLessonLogs />} />
                  <Route path="practice-booking" element={<UserPracticeBooking />} />
                  <Route path="lesson-schedule" element={<UserLessonSchedule />} />
                  <Route path="change-password" element={<ChangePassword />} />
                </Route>
              </Routes>
            </div>
            <ConditionalFooter />
            <Toaster
              position="top-center"
              reverseOrder={false}
              gutter={8}
              containerClassName=""
              containerStyle={{}}
              toastOptions={{
                // 기본 옵션
                duration: 4000,
                style: {
                  background: '#ffffff',
                  color: '#1e293b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '280px',
                  maxWidth: '400px',
                },
                // 성공 토스트
                success: {
                  duration: 3000,
                  style: {
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    minWidth: '280px',
                    maxWidth: '400px',
                  },
                  iconTheme: {
                    primary: '#ffffff',
                    secondary: '#10b981',
                  },
                },
                // 에러 토스트
                error: {
                  duration: 5000,
                  style: {
                    background: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    minWidth: '280px',
                    maxWidth: '400px',
                  },
                  iconTheme: {
                    primary: '#ffffff',
                    secondary: '#ef4444',
                  },
                },
              }}
            />
        </AuthProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App; 